import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useToast } from '../components/ToastContainer';
import { format, eachDayOfInterval } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import * as globalWorkLogApi from '../services/api/globalWorkLog';
import GlobalIssueSelector from '../components/worklog/GlobalIssueSelector';
import { PlusIcon, TrashIcon, DocumentDuplicateIcon, ArrowLeftIcon, CloudIcon } from '@heroicons/react/24/outline';
import { TAIWAN_CITIES, DEFAULT_POSITIONS, AttendanceTable, AttendancePosition, WeatherData } from '../types/worklog';

interface GlobalWorkLog {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  organizationName?: string;
  communityName?: string; // 資料庫實際存儲的欄位
  managementCompany?: string;
  dutyStaff?: string;
  staffAttendance?: any;
  equipmentStatus?: any;
  otherWorkItems?: any;
  weatherLocation?: string;
  weatherData?: WeatherData | null;
  attendanceTable?: AttendanceTable;
  selectedIssues: Array<{
    id: string;
    groupId?: string;
    issue: {
      id: string;
      key: string;
      title: string;
    };
  }>;
  issueGroups?: Array<{
    id: string;
    title: string;
    sortOrder: number;
    issues: Array<{
      id: string;
      issue: {
        id: string;
        key: string;
        title: string;
      };
    }>;
  }>;
}

interface IssueGroup {
  id?: string;
  title: string;
  selectedIssueIds: string[];
  isNew?: boolean;
}

const GlobalWorkLogEditor: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();
  const isEditing = Boolean(id);
  const copyFromId = searchParams.get('copyFrom');

  const [workLog, setWorkLog] = useState<GlobalWorkLog | null>(null);
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [isIssueSelectorOpen, setIsIssueSelectorOpen] = useState(false);
  const [weatherError, setWeatherError] = useState<string | null>(null);
  
  // 自定義議題分組狀態
  const [issueGroups, setIssueGroups] = useState<IssueGroup[]>([]);
  const [activeGroupSelector, setActiveGroupSelector] = useState<number | null>(null);
  
  // 自定義設備系統狀態
  const [customSystems, setCustomSystems] = useState<Array<{name: string, status: string, notes: string}>>([]);
  
  // 新增的狀態
  const [weatherLocation, setWeatherLocation] = useState<string>('');
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [isLoadingWeather, setIsLoadingWeather] = useState(false);
  const [manualWeather, setManualWeather] = useState({ weather: '', minTemp: '', maxTemp: '' });
  const [attendanceTable, setAttendanceTable] = useState<AttendanceTable>({
    positions: DEFAULT_POSITIONS.map(title => ({
      title,
      requiredCount: 0,
      actualCount: 0,
      leaveCount: 0,
      attendanceStatus: '在勤',
      workingHours: { start: '', end: '' }
    })),
    totalRequired: 0,
    totalActual: 0,
    totalLeave: 0
  });

  // 表單資料
  const [formData, setFormData] = useState({
    title: `${format(new Date(), 'yyyy/MM/dd', { locale: zhTW })} 工作日誌`,
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    organizationName: '',
    managementCompany: '',
    dutyStaff: '',
    staffAttendance: {
      totalStaff: '',
      presentStaff: '',
      notes: ''
    },
    equipmentStatus: {
      elevatorSystem: {
        status: '正常',
        notes: ''
      },
      waterPumpSystem: {
        status: '正常', 
        notes: ''
      },
      powerSystem: {
        status: '正常',
        notes: ''
      },
      communityFireSafety: {
        totalIsolations: '',
        todayNewIsolations: '',
        todayRepaired: '',
        status: '正常',
        notes: ''
      },
      customSystems: [],
      notes: ''
    },
    otherWorkItems: {
      cleaningWork: '',
      maintenanceWork: '',
      securityPatrol: '',
      visitorManagement: '',
      emergencyHandling: '',
      otherTasks: ''
    }
  });

  const [selectedIssueIds, setSelectedIssueIds] = useState<string[]>([]);
  const [availableWorkLogs, setAvailableWorkLogs] = useState<GlobalWorkLog[]>([]);

  useEffect(() => {
    if (isEditing && id) {
      loadWorkLog();
    } else if (copyFromId) {
      loadWorkLogToCopy();
    }
    
    // 載入可用的工作日誌列表（用於複製功能）
    if (!isEditing) {
      loadAvailableWorkLogs();
    }
  }, [isEditing, id, copyFromId]);

  const loadWorkLog = async () => {
    try {
      setIsLoading(true);
      const data = await globalWorkLogApi.getGlobalWorkLog(id!) as GlobalWorkLog;
      setWorkLog(data);
      
      // 填充表單資料
      setFormData({
        title: data.title,
        startDate: format(new Date(data.startDate), 'yyyy-MM-dd'),
        endDate: format(new Date(data.endDate), 'yyyy-MM-dd'),
        organizationName: data.organizationName || data.communityName || '',
        managementCompany: data.managementCompany || '',
        dutyStaff: data.dutyStaff || '',
        staffAttendance: data.staffAttendance || {
          totalStaff: '',
          presentStaff: '',
          notes: ''
        },
        equipmentStatus: {
          elevatorSystem: data.equipmentStatus?.elevatorSystem || {
            status: '正常',
            notes: ''
          },
          waterPumpSystem: data.equipmentStatus?.waterPumpSystem || {
            status: '正常',
            notes: ''
          },
          powerSystem: data.equipmentStatus?.powerSystem || {
            status: '正常',
            notes: ''
          },
          communityFireSafety: data.equipmentStatus?.communityFireSafety || {
            totalIsolations: '',
            todayNewIsolations: '',
            todayRepaired: '',
            status: '正常',
            notes: ''
          },
          customSystems: data.equipmentStatus?.customSystems || [],
          notes: data.equipmentStatus?.notes || ''
        },
        otherWorkItems: data.otherWorkItems || {
          cleaningWork: '',
          maintenanceWork: '',
          securityPatrol: '',
          visitorManagement: '',
          emergencyHandling: '',
          otherTasks: ''
        }
      });

      setSelectedIssueIds(data.selectedIssues.map(si => si.issue.id));

      // 載入自定義議題分組
      if (data.issueGroups) {
        const loadedGroups = data.issueGroups.map(group => ({
          id: group.id,
          title: group.title,
          selectedIssueIds: group.issues.map(issue => issue.issue.id),
          isNew: false
        }));
        setIssueGroups(loadedGroups);
      }
      
      // 載入自定義設備系統
      if (data.equipmentStatus?.customSystems) {
        setCustomSystems(data.equipmentStatus.customSystems);
      }
      
      // 載入天氣和出勤資料
      if (data.weatherLocation) {
        setWeatherLocation(data.weatherLocation);
      }
      if (data.weatherData) {
        setWeatherData(data.weatherData);
        setManualWeather({
          weather: data.weatherData.weather || '',
          minTemp: String(data.weatherData.temperature?.min ?? ''),
          maxTemp: String(data.weatherData.temperature?.max ?? ''),
        });
        setWeatherError(null);
      }
      if (data.attendanceTable) {
        setAttendanceTable(data.attendanceTable);
      }
    } catch (error) {
      console.error('Failed to load global work log:', error);
      showError('載入全域工作日誌失敗');
      navigate('/global-worklogs');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableWorkLogs = async () => {
    try {
      const data = await globalWorkLogApi.getGlobalWorkLogs();
      setAvailableWorkLogs((data as GlobalWorkLog[]).slice(0, 10)); // 限制顯示最近10個
    } catch (error) {
      console.error('Failed to load available work logs:', error);
    }
  };

  const loadWorkLogToCopy = async () => {
    try {
      setIsLoading(true);
      const data = await globalWorkLogApi.getGlobalWorkLog(copyFromId!) as GlobalWorkLog;
      
      // 複製工作日誌內容，但保持今天的日期和新的標題
      const todayDate = format(new Date(), 'yyyy-MM-dd');
      const todayTitle = `${format(new Date(), 'yyyy/MM/dd', { locale: zhTW })} 工作日誌`;
      
      setFormData({
        title: todayTitle,
        startDate: todayDate,
        endDate: todayDate,
        organizationName: data.organizationName || data.communityName || '',
        managementCompany: data.managementCompany || '',
        dutyStaff: data.dutyStaff || '',
        staffAttendance: data.staffAttendance || {
          totalStaff: '',
          presentStaff: '',
          notes: ''
        },
        equipmentStatus: {
          elevatorSystem: data.equipmentStatus?.elevatorSystem || {
            status: '正常',
            notes: ''
          },
          waterPumpSystem: data.equipmentStatus?.waterPumpSystem || {
            status: '正常',
            notes: ''
          },
          powerSystem: data.equipmentStatus?.powerSystem || {
            status: '正常',
            notes: ''
          },
          communityFireSafety: data.equipmentStatus?.communityFireSafety || {
            totalIsolations: '',
            todayNewIsolations: '',
            todayRepaired: '',
            status: '正常',
            notes: ''
          },
          customSystems: data.equipmentStatus?.customSystems || [],
          notes: data.equipmentStatus?.notes || ''
        },
        otherWorkItems: data.otherWorkItems || {
          cleaningWork: '',
          maintenanceWork: '',
          securityPatrol: '',
          visitorManagement: '',
          emergencyHandling: '',
          otherTasks: ''
        }
      });

      setSelectedIssueIds(data.selectedIssues.map(si => si.issue.id));

      // 載入自定義議題分組
      if (data.issueGroups) {
        const loadedGroups = data.issueGroups.map(group => ({
          id: undefined, // 新建時不保留原ID
          title: group.title,
          selectedIssueIds: group.issues.map(issue => issue.issue.id),
          isNew: true
        }));
        setIssueGroups(loadedGroups);
      }
      
      // 載入自定義設備系統
      if (data.equipmentStatus?.customSystems) {
        setCustomSystems(data.equipmentStatus.customSystems);
      }
      
      // 載入天氣和出勤資料（複製時也包含這些資料）
      if (data.weatherLocation) {
        setWeatherLocation(data.weatherLocation);
      }
      if (data.attendanceTable) {
        setAttendanceTable(data.attendanceTable);
      }
      // 天氣資料不複製，需要根據新的日期重新獲取

      showSuccess(`已複製「${data.title}」的內容`);
    } catch (error) {
      console.error('Failed to load work log to copy:', error);
      showError('載入工作日誌複製內容失敗');
      navigate('/global-worklogs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  type NestedSection = 'staffAttendance' | 'equipmentStatus' | 'otherWorkItems'

  const handleNestedInputChange = (section: NestedSection, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] as Record<string, any>),
        [field]: value,
      },
    }));
  };

  // 計算星期幾
  const getWeekdaysString = (startDate: string, endDate: string): string => {
    if (!startDate || !endDate) return '';
    
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      if (start > end) return '';
      
      const days = eachDayOfInterval({ start, end });
      const weekdays = days.map(day => format(day, 'EEEE', { locale: zhTW }));
      
      // 去重並保持順序
      const uniqueWeekdays = [...new Set(weekdays)];
      return uniqueWeekdays.join('；');
    } catch (error) {
      return '';
    }
  };

  // 載入天氣資料
  const fetchWeatherData = async (location: string, date: string) => {
    if (!location || !date) return;
    
    setIsLoadingWeather(true);
    try {
      const weather = await globalWorkLogApi.getWeatherData(location, date);
      setWeatherData(weather);
      setWeatherError(null);
      setManualWeather({
        weather: weather.weather || '',
        minTemp: String(weather.temperature?.min ?? ''),
        maxTemp: String(weather.temperature?.max ?? ''),
      });
    } catch (error) {
      console.error('Failed to fetch weather:', error);
      setWeatherData(null);
      setWeatherError('自動截取天氣失敗，請手動填寫');
      showError('無法取得天氣資料，請改為手動填寫');
    } finally {
      setIsLoadingWeather(false);
    }
  };

  const applyManualWeather = () => {
    if (!weatherLocation) {
      showError('請先選擇天氣地區');
      return;
    }
    if (!formData.startDate) {
      showError('請先選擇日期');
      return;
    }
    if (!manualWeather.weather || !manualWeather.minTemp || !manualWeather.maxTemp) {
      showError('請完整填寫天氣描述與溫度');
      return;
    }

    const min = Number.parseInt(manualWeather.minTemp, 10);
    const max = Number.parseInt(manualWeather.maxTemp, 10);

    if (Number.isNaN(min) || Number.isNaN(max)) {
      showError('溫度需為數字');
      return;
    }

    const cityLabel = TAIWAN_CITIES.find(c => c.value === weatherLocation)?.label ?? weatherLocation;

    const manualData: WeatherData = {
      location: cityLabel,
      date: formData.startDate,
      weather: manualWeather.weather,
      temperature: { min, max },
      icon: '✏️',
      description: `${manualWeather.weather} ${min}°C - ${max}°C`,
    };

    setWeatherData(manualData);
    setWeatherError(null);
    showSuccess('已套用手動天氣');
  };

  const clearWeather = () => {
    setWeatherData(null);
    setWeatherError(null);
    setManualWeather({ weather: '', minTemp: '', maxTemp: '' });
  };

  // 更新出勤表格
  const updateAttendancePosition = (index: number, field: keyof AttendancePosition | 'workingHours', value: any) => {
    setAttendanceTable(prev => {
      const newPositions = [...prev.positions];
      
      if (field === 'workingHours') {
        newPositions[index] = {
          ...newPositions[index],
          workingHours: value
        };
      } else {
        newPositions[index] = {
          ...newPositions[index],
          [field]: value
        };
      }
      
      // 計算總計
      const totalRequired = newPositions.reduce((sum, pos) => sum + (Number(pos.requiredCount) || 0), 0);
      const totalActual = newPositions.reduce((sum, pos) => sum + (Number(pos.actualCount) || 0), 0);
      const totalLeave = newPositions.reduce((sum, pos) => sum + (Number(pos.leaveCount) || 0), 0);
      
      return {
        positions: newPositions,
        totalRequired,
        totalActual,
        totalLeave
      };
    });
  };

  // 新增職位
  const addAttendancePosition = () => {
    const newPosition: AttendancePosition = {
      title: '',
      requiredCount: 0,
      actualCount: 0,
      leaveCount: 0,
      attendanceStatus: '在勤',
      workingHours: { start: '', end: '' }
    };
    
    setAttendanceTable(prev => ({
      ...prev,
      positions: [...prev.positions, newPosition]
    }));
  };

  // 刪除職位
  const removeAttendancePosition = (index: number) => {
    setAttendanceTable(prev => {
      const newPositions = prev.positions.filter((_, i) => i !== index);
      
      // 重新計算總計
      const totalRequired = newPositions.reduce((sum, pos) => sum + (Number(pos.requiredCount) || 0), 0);
      const totalActual = newPositions.reduce((sum, pos) => sum + (Number(pos.actualCount) || 0), 0);
      const totalLeave = newPositions.reduce((sum, pos) => sum + (Number(pos.leaveCount) || 0), 0);
      
      return {
        positions: newPositions,
        totalRequired,
        totalActual,
        totalLeave
      };
    });
  };

  // 監聽日期和地區變化以更新天氣
  useEffect(() => {
    if (weatherLocation && formData.startDate) {
      fetchWeatherData(weatherLocation, formData.startDate);
    }
  }, [weatherLocation, formData.startDate]);

  const handleSave = async (status: 'DRAFT' | 'PUBLISHED' = 'DRAFT') => {
    if (!formData.title) {
      showError('請輸入工作日誌標題');
      return;
    }

    if (!formData.startDate || !formData.endDate) {
      showError('請選擇開始和結束日期');
      return;
    }

    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      showError('開始日期不能晚於結束日期');
      return;
    }

    // 檢查是否有未儲存的議題分組
    const unsavedGroups = issueGroups.filter(group => group.isNew);
    if (unsavedGroups.length > 0) {
      const groupTitles = unsavedGroups.map(group => group.title || '未命名分組').join('、');
      showError(`請先儲存以下 ${unsavedGroups.length} 個議題分組：${groupTitles}。您可以點擊下方的「儲存所有分組」按鈕一次性儲存所有分組。`);
      return;
    }

    try {
      setIsSaving(true);
      
      const data = {
        ...formData,
        equipmentStatus: {
          ...formData.equipmentStatus,
          customSystems
        },
        weatherLocation,
        weatherData,
        attendanceTable,
        status
      };

      // 調試：檢查發送的數據
      console.log('發送工作日誌數據:', {
        requestedStatus: status,
        isEditing,
        workLogId: id
      });

      let savedWorkLog;
      if (isEditing) {
        savedWorkLog = await globalWorkLogApi.updateGlobalWorkLog(id!, data);
      } else {
        savedWorkLog = await globalWorkLogApi.createGlobalWorkLog(data);
      }

      // 同步預設議題分組的議題列表
      if (isEditing && workLog) {
        // 獲取當前的議題IDs（預設分組的議題，不包括自定義分組）
        const currentDefaultIssueIds = workLog.selectedIssues
          .filter(si => !si.groupId) // 只取預設分組的議題
          .map(si => si.issue.id);
        
        // 同步議題列表
        await globalWorkLogApi.syncWorkLogIssues(savedWorkLog.id, selectedIssueIds, currentDefaultIssueIds);
      } else if (selectedIssueIds.length > 0) {
        // 新建時直接添加議題
        await globalWorkLogApi.addIssuesToGlobalWorkLog(savedWorkLog.id, selectedIssueIds);
      }

      // 如果是新建模式，創建所有準備好的自定義議題分組
      if (!isEditing) {
        const pendingGroups = issueGroups.filter(group => !group.isNew && !group.id);
        for (const group of pendingGroups) {
          if (group.title.trim() && group.selectedIssueIds.length > 0) {
            try {
              const result = await globalWorkLogApi.createIssueGroup(savedWorkLog.id, {
                title: group.title,
                issueIds: group.selectedIssueIds
              });
              console.log('Created issue group:', result.issueGroup.title);
            } catch (error) {
              console.error('Failed to create issue group:', group.title, error);
              // 不中斷流程，只記錄錯誤
            }
          }
        }

        if (pendingGroups.length > 0) {
          showSuccess(`全域工作日誌已建立，並成功建立 ${pendingGroups.length} 個議題分組`);
        } else {
          showSuccess('全域工作日誌已建立');
        }
      } else {
        showSuccess('全域工作日誌已更新');
      }
      
      navigate('/global-worklogs');
    } catch (error) {
      console.error('Failed to save global work log:', error);
      showError(isEditing ? '更新全域工作日誌失敗' : '建立全域工作日誌失敗');
    } finally {
      setIsSaving(false);
    }
  };

  const handleIssueSelection = (issueIds: string[]) => {
    setSelectedIssueIds(issueIds);
  };

  // 自定義議題分組管理函數
  const addNewIssueGroup = () => {
    const newGroup: IssueGroup = {
      title: '執行概況',
      selectedIssueIds: [],
      isNew: true
    };
    setIssueGroups(prev => [...prev, newGroup]);
  };

  const updateIssueGroupTitle = (index: number, title: string) => {
    setIssueGroups(prev => 
      prev.map((group, i) => 
        i === index ? { ...group, title } : group
      )
    );
  };

  const deleteIssueGroup = async (index: number) => {
    const group = issueGroups[index];
    
    // 如果是已存在的分組，需要調用 API 刪除
    if (group.id && !group.isNew && id) {
      try {
        await globalWorkLogApi.deleteIssueGroup(id, group.id);
        showSuccess('議題分組已刪除');
      } catch (error) {
        console.error('Failed to delete issue group:', error);
        showError('刪除議題分組失敗');
        return;
      }
    }

    setIssueGroups(prev => prev.filter((_, i) => i !== index));
  };

  const handleGroupIssueSelection = (groupIndex: number, issueIds: string[]) => {
    setIssueGroups(prev =>
      prev.map((group, i) =>
        i === groupIndex ? { ...group, selectedIssueIds: issueIds } : group
      )
    );
    setActiveGroupSelector(null);
  };

  // 自定義設備系統管理函數
  const addCustomSystem = () => {
    const newSystem = { name: '', status: '正常', notes: '' };
    setCustomSystems(prev => [...prev, newSystem]);
  };

  const updateCustomSystem = (index: number, field: string, value: string) => {
    setCustomSystems(prev => prev.map((system, i) => 
      i === index ? { ...system, [field]: value } : system
    ));
  };

  const deleteCustomSystem = (index: number) => {
    setCustomSystems(prev => prev.filter((_, i) => i !== index));
  };

  // 取得未儲存的議題分組數量
  const getUnsavedGroupsCount = () => {
    return issueGroups.filter(group => group.isNew).length;
  };

  // 儲存所有未儲存的議題分組
  const saveAllUnsavedGroups = async () => {
    const unsavedGroups = issueGroups.filter(group => group.isNew);
    
    if (unsavedGroups.length === 0) {
      return;
    }

    let savedCount = 0;
    
    // 遍歷所有分組並儲存未儲存的
    for (let i = 0; i < issueGroups.length; i++) {
      const group = issueGroups[i];
      if (group.isNew) {
        // 驗證分組是否有效
        if (!group.title.trim()) {
          showError(`第 ${i + 1} 個分組缺少標題，請先填寫標題`);
          return;
        }
        if (group.selectedIssueIds.length === 0) {
          showError(`分組「${group.title}」尚未選擇議題，請先選擇議題`);
          return;
        }
        
        try {
          await saveIssueGroup(i);
          savedCount++;
        } catch (error) {
          console.error('Failed to save group:', group.title, error);
          showError(`儲存分組「${group.title}」失敗`);
          return;
        }
      }
    }
    
    if (savedCount > 0) {
      showSuccess(`已成功儲存 ${savedCount} 個議題分組`);
    }
  };

  const saveIssueGroup = async (groupIndex: number) => {
    const group = issueGroups[groupIndex];
    
    if (!group.title.trim()) {
      showError('請輸入分組標題');
      return;
    }

    if (group.selectedIssueIds.length === 0) {
      showError('請選擇至少一個議題');
      return;
    }

    // 如果是新建工作日誌模式，只需要在本地狀態中標記為已儲存
    if (!id) {
      setIssueGroups(prev =>
        prev.map((g, i) =>
          i === groupIndex ? { ...g, isNew: false } : g
        )
      );
      showSuccess('議題分組已準備完成，將在儲存工作日誌時一併建立');
      return;
    }

    try {
      if (group.isNew || !group.id) {
        // 創建新分組
        const result = await globalWorkLogApi.createIssueGroup(id, {
          title: group.title,
          issueIds: group.selectedIssueIds
        });
        
        // 更新本地狀態
        setIssueGroups(prev =>
          prev.map((g, i) =>
            i === groupIndex ? { ...g, id: result.issueGroup.id, isNew: false } : g
          )
        );
        
        showSuccess('議題分組已建立');
      } else {
        // 更新現有分組：標題和議題列表
        await globalWorkLogApi.updateIssueGroup(id, group.id, { title: group.title });
        
        // 同步該分組的議題列表
        await globalWorkLogApi.syncIssueGroupIssues(id, group.id, group.selectedIssueIds);
        
        showSuccess('議題分組已更新');
      }
    } catch (error) {
      console.error('Failed to save issue group:', error);
      showError('儲存議題分組失敗');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* 標題 */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/global-worklogs')}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
          title="返回工作日誌列表"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? '編輯全域工作日誌' : '新增全域工作日誌'}
          </h1>
          <p className="text-gray-600 mt-1">
            建立跨專案的工作日誌，整合多個專案的議題和工作項目
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 space-y-6">
          {/* 複製功能 - 只在新增模式顯示 */}
          {!isEditing && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <DocumentDuplicateIcon className="h-5 w-5 mr-2" />
                複製現有工作日誌
              </h3>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-700 mb-3">
                  您可以選擇複製現有工作日誌的內容（開始結束日期會自動設為今日）
                </p>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-blue-800">
                    選擇要複製的工作日誌
                  </label>
                  <select
                    value={copyFromId || ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        navigate(`/global-worklogs/create?copyFrom=${e.target.value}`);
                      }
                    }}
                    className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">請選擇工作日誌...</option>
                    {availableWorkLogs.map((workLog) => (
                      <option key={workLog.id} value={workLog.id}>
                        {workLog.title} ({format(new Date(workLog.startDate), 'yyyy/MM/dd', { locale: zhTW })})
                      </option>
                    ))}
                  </select>
                </div>
                
                {copyFromId && (
                  <div className="mt-2 text-sm text-green-700">
                    ✓ 已複製選定工作日誌的內容
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 基本資訊 */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">基本資訊</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  工作日誌標題 *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="請輸入工作日誌標題"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  組織名稱
                </label>
                <input
                  type="text"
                  value={formData.organizationName}
                  onChange={(e) => handleInputChange('organizationName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="請輸入組織名稱"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  開始日期 *
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  結束日期 *
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  星期
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  {getWeekdaysString(formData.startDate, formData.endDate) || '請選擇日期'}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  天氣地區
                </label>
                <select
                  value={weatherLocation}
                  onChange={(e) => setWeatherLocation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">請選擇地區...</option>
                  {TAIWAN_CITIES.map(city => (
                    <option key={city.value} value={city.value}>
                      {city.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2 rounded-md border border-dashed border-blue-200 bg-blue-50 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-blue-900">
                    <CloudIcon className="h-5 w-5" />
                    <span className="text-sm font-semibold">天氣資訊</span>
                    {weatherError && (
                      <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                        {weatherError}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (weatherLocation && formData.startDate) {
                          fetchWeatherData(weatherLocation, formData.startDate);
                        } else {
                          showError('請先選擇地區與日期');
                        }
                      }}
                      className="inline-flex items-center gap-1 rounded-md border border-blue-200 px-2 py-1 text-xs text-blue-700 hover:bg-blue-100"
                    >
                      重新擷取
                    </button>
                    <button
                      type="button"
                      onClick={clearWeather}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                    >
                      清除天氣
                    </button>
                  </div>
                </div>

                {weatherData && (
                  <div className="mt-3 flex items-center gap-3 rounded-md bg-white px-3 py-2 shadow-sm">
                    <span className="text-2xl">{weatherData.icon}</span>
                    <div className="text-sm text-gray-800">
                      <div className="font-medium">
                        {weatherData.location} - {weatherData.weather}
                      </div>
                      <div className="text-gray-600">
                        溫度：{weatherData.temperature.min}°C - {weatherData.temperature.max}°C
                      </div>
                    </div>
                  </div>
                )}

                {(!weatherData || weatherError) && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">天氣描述</label>
                      <input
                        type="text"
                        value={manualWeather.weather}
                        onChange={e => setManualWeather(prev => ({ ...prev, weather: e.target.value }))}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="例：多雲、陣雨"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">最低溫 (°C)</label>
                      <input
                        type="number"
                        value={manualWeather.minTemp}
                        onChange={e => setManualWeather(prev => ({ ...prev, minTemp: e.target.value }))}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="20"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">最高溫 (°C)</label>
                      <input
                        type="number"
                        value={manualWeather.maxTemp}
                        onChange={e => setManualWeather(prev => ({ ...prev, maxTemp: e.target.value }))}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="30"
                      />
                    </div>
                    <div className="md:col-span-3 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={applyManualWeather}
                        className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                        disabled={isLoadingWeather}
                      >
                        套用手動天氣
                      </button>
                      {isLoadingWeather && (
                        <span className="text-xs text-gray-500">正在重新擷取...</span>
                      )}
                    </div>
                  </div>
                )}

                {isLoadingWeather && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-gray-600">
                    <div className="h-3 w-3 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                    正在載入天氣資料...
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  管理公司
                </label>
                <input
                  type="text"
                  value={formData.managementCompany}
                  onChange={(e) => handleInputChange('managementCompany', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="請輸入管理公司名稱"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  值班人員
                </label>
                <input
                  type="text"
                  value={formData.dutyStaff}
                  onChange={(e) => handleInputChange('dutyStaff', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="請輸入值班人員姓名"
                />
              </div>
            </div>
          </div>

          {/* 人員出勤狀況 */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">人員出勤狀況</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                出勤備註
              </label>
              <textarea
                value={formData.staffAttendance.notes}
                onChange={(e) => handleNestedInputChange('staffAttendance', 'notes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="請輸入出勤相關備註"
              />
            </div>
          </div>

          {/* 出勤表格 */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">出勤表格</h3>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      職稱
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      應到人數
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      實到人數
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      休假人數
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      今日出勤人員
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      上班時間
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attendanceTable.positions.map((position, index) => (
                    <tr key={index}>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={position.title}
                          onChange={(e) => updateAttendancePosition(index, 'title', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="職稱"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={position.requiredCount}
                          onChange={(e) => updateAttendancePosition(index, 'requiredCount', parseInt(e.target.value) || 0)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                          min="0"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={position.actualCount}
                          onChange={(e) => updateAttendancePosition(index, 'actualCount', parseInt(e.target.value) || 0)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                          min="0"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={position.leaveCount}
                          onChange={(e) => updateAttendancePosition(index, 'leaveCount', parseInt(e.target.value) || 0)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                          min="0"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={position.attendanceStatus}
                          onChange={(e) => updateAttendancePosition(index, 'attendanceStatus', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="在勤"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-col space-y-1">
                          <input
                            type="text"
                            value={position.workingHours.start}
                            onChange={(e) => updateAttendancePosition(index, 'workingHours', {
                              ...position.workingHours,
                              start: e.target.value
                            })}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                            placeholder="08:00"
                          />
                          <div className="border-t border-gray-300"></div>
                          <input
                            type="text"
                            value={position.workingHours.end}
                            onChange={(e) => updateAttendancePosition(index, 'workingHours', {
                              ...position.workingHours,
                              end: e.target.value
                            })}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                            placeholder="17:00"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => removeAttendancePosition(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {/* 合計列 */}
                  <tr className="bg-gray-50 font-medium">
                    <td className="px-4 py-2">合計</td>
                    <td className="px-4 py-2 text-center">{attendanceTable.totalRequired}</td>
                    <td className="px-4 py-2 text-center">{attendanceTable.totalActual}</td>
                    <td className="px-4 py-2 text-center">{attendanceTable.totalLeave}</td>
                    <td className="px-4 py-2" colSpan={3}></td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <button
              type="button"
              onClick={addAttendancePosition}
              className="mt-2 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              新增職位
            </button>
          </div>

          {/* 設備狀況 */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">設備狀況</h3>
            
            {/* 固定系統 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* 電梯系統 */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  電梯系統
                </label>
                <select
                  value={formData.equipmentStatus.elevatorSystem.status}
                  onChange={(e) => {
                    const newStatus = {
                      ...formData.equipmentStatus,
                      elevatorSystem: {
                        ...formData.equipmentStatus.elevatorSystem,
                        status: e.target.value
                      }
                    };
                    handleInputChange('equipmentStatus', newStatus);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="正常">正常</option>
                  <option value="異常">異常</option>
                  <option value="待料中">待料中</option>
                  <option value="維修中">維修中</option>
                  <option value="其他情況">其他情況</option>
                </select>
                <textarea
                  value={formData.equipmentStatus.elevatorSystem.notes}
                  onChange={(e) => {
                    const newStatus = {
                      ...formData.equipmentStatus,
                      elevatorSystem: {
                        ...formData.equipmentStatus.elevatorSystem,
                        notes: e.target.value
                      }
                    };
                    handleInputChange('equipmentStatus', newStatus);
                  }}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="備註"
                />
              </div>
              
              {/* 供水系統 */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  供水系統
                </label>
                <select
                  value={formData.equipmentStatus.waterPumpSystem.status}
                  onChange={(e) => {
                    const newStatus = {
                      ...formData.equipmentStatus,
                      waterPumpSystem: {
                        ...formData.equipmentStatus.waterPumpSystem,
                        status: e.target.value
                      }
                    };
                    handleInputChange('equipmentStatus', newStatus);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="正常">正常</option>
                  <option value="異常">異常</option>
                  <option value="待料中">待料中</option>
                  <option value="維修中">維修中</option>
                  <option value="其他情況">其他情況</option>
                </select>
                <textarea
                  value={formData.equipmentStatus.waterPumpSystem.notes}
                  onChange={(e) => {
                    const newStatus = {
                      ...formData.equipmentStatus,
                      waterPumpSystem: {
                        ...formData.equipmentStatus.waterPumpSystem,
                        notes: e.target.value
                      }
                    };
                    handleInputChange('equipmentStatus', newStatus);
                  }}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="備註"
                />
              </div>
              
              {/* 電力系統 */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  電力系統
                </label>
                <select
                  value={formData.equipmentStatus.powerSystem.status}
                  onChange={(e) => {
                    const newStatus = {
                      ...formData.equipmentStatus,
                      powerSystem: {
                        ...formData.equipmentStatus.powerSystem,
                        status: e.target.value
                      }
                    };
                    handleInputChange('equipmentStatus', newStatus);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="正常">正常</option>
                  <option value="異常">異常</option>
                  <option value="待料中">待料中</option>
                  <option value="維修中">維修中</option>
                  <option value="其他情況">其他情況</option>
                </select>
                <textarea
                  value={formData.equipmentStatus.powerSystem.notes}
                  onChange={(e) => {
                    const newStatus = {
                      ...formData.equipmentStatus,
                      powerSystem: {
                        ...formData.equipmentStatus.powerSystem,
                        notes: e.target.value
                      }
                    };
                    handleInputChange('equipmentStatus', newStatus);
                  }}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="備註"
                />
              </div>
            </div>
            
            {/* 自定義系統 */}
            {customSystems.map((system, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <input
                    type="text"
                    value={system.name}
                    onChange={(e) => updateCustomSystem(index, 'name', e.target.value)}
                    placeholder="系統名稱"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={() => deleteCustomSystem(index)}
                    className="ml-2 p-1 text-red-600 hover:bg-red-50 rounded"
                    title="刪除系統"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select
                    value={system.status}
                    onChange={(e) => updateCustomSystem(index, 'status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="正常">正常</option>
                    <option value="異常">異常</option>
                    <option value="待料中">待料中</option>
                    <option value="維修中">維修中</option>
                    <option value="其他情況">其他情況</option>
                  </select>
                  <textarea
                    value={system.notes}
                    onChange={(e) => updateCustomSystem(index, 'notes', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="備註"
                  />
                </div>
              </div>
            ))}
            
            {/* 添加自定義系統按鈕 */}
            <div className="text-center">
              <button
                onClick={addCustomSystem}
                className="inline-flex items-center px-4 py-2 text-sm text-gray-600 bg-white border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 hover:text-gray-700"
                type="button"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                新增設備系統
              </button>
            </div>

            {/* 社區消防區塊 */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-medium text-gray-900 mb-3">社區消防</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    總共隔離 (處)
                  </label>
                  <input
                    type="number"
                    value={formData.equipmentStatus.communityFireSafety.totalIsolations}
                    onChange={(e) => {
                      const newStatus = {
                        ...formData.equipmentStatus,
                        communityFireSafety: {
                          ...formData.equipmentStatus.communityFireSafety,
                          totalIsolations: e.target.value
                        }
                      };
                      handleInputChange('equipmentStatus', newStatus);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="請輸入總隔離處數"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    今日新增 (處)
                  </label>
                  <input
                    type="number"
                    value={formData.equipmentStatus.communityFireSafety.todayNewIsolations}
                    onChange={(e) => {
                      const newStatus = {
                        ...formData.equipmentStatus,
                        communityFireSafety: {
                          ...formData.equipmentStatus.communityFireSafety,
                          todayNewIsolations: e.target.value
                        }
                      };
                      handleInputChange('equipmentStatus', newStatus);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="請輸入今日新增處數"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    今日修復 (處)
                  </label>
                  <input
                    type="number"
                    value={formData.equipmentStatus.communityFireSafety.todayRepaired}
                    onChange={(e) => {
                      const newStatus = {
                        ...formData.equipmentStatus,
                        communityFireSafety: {
                          ...formData.equipmentStatus.communityFireSafety,
                          todayRepaired: e.target.value
                        }
                      };
                      handleInputChange('equipmentStatus', newStatus);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="請輸入今日修復處數"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    消防狀態
                  </label>
                  <select
                    value={formData.equipmentStatus.communityFireSafety.status}
                    onChange={(e) => {
                      const newStatus = {
                        ...formData.equipmentStatus,
                        communityFireSafety: {
                          ...formData.equipmentStatus.communityFireSafety,
                          status: e.target.value
                        }
                      };
                      handleInputChange('equipmentStatus', newStatus);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="正常">正常</option>
                    <option value="異常">異常</option>
                    <option value="待料中">待料中</option>
                    <option value="維修中">維修中</option>
                    <option value="其他情況">其他情況</option>
                  </select>
                </div>
              </div>
              
              {/* 消防備註 */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  消防備註
                </label>
                <textarea
                  value={formData.equipmentStatus.communityFireSafety.notes}
                  onChange={(e) => {
                    const newStatus = {
                      ...formData.equipmentStatus,
                      communityFireSafety: {
                        ...formData.equipmentStatus.communityFireSafety,
                        notes: e.target.value
                      }
                    };
                    handleInputChange('equipmentStatus', newStatus);
                  }}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="請輸入消防相關備註"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                設備備註
              </label>
              <textarea
                value={formData.equipmentStatus.notes}
                onChange={(e) => handleNestedInputChange('equipmentStatus', 'notes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="請輸入設備相關備註"
              />
            </div>
          </div>

          {/* 其他工作項目 */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">其他工作項目</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  清潔工作
                </label>
                <textarea
                  value={formData.otherWorkItems.cleaningWork}
                  onChange={(e) => handleNestedInputChange('otherWorkItems', 'cleaningWork', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="請輸入清潔工作內容"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  維修工作
                </label>
                <textarea
                  value={formData.otherWorkItems.maintenanceWork}
                  onChange={(e) => handleNestedInputChange('otherWorkItems', 'maintenanceWork', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="請輸入維修工作內容"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  安全巡邏
                </label>
                <textarea
                  value={formData.otherWorkItems.securityPatrol}
                  onChange={(e) => handleNestedInputChange('otherWorkItems', 'securityPatrol', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="請輸入安全巡邏內容"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  訪客管理
                </label>
                <textarea
                  value={formData.otherWorkItems.visitorManagement}
                  onChange={(e) => handleNestedInputChange('otherWorkItems', 'visitorManagement', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="請輸入訪客管理內容"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  緊急處理
                </label>
                <textarea
                  value={formData.otherWorkItems.emergencyHandling}
                  onChange={(e) => handleNestedInputChange('otherWorkItems', 'emergencyHandling', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="請輸入緊急處理內容"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  其他任務
                </label>
                <textarea
                  value={formData.otherWorkItems.otherTasks}
                  onChange={(e) => handleNestedInputChange('otherWorkItems', 'otherTasks', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="請輸入其他任務內容"
                />
              </div>
            </div>
          </div>

          {/* 議題關聯 */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">報告議題分組</h3>
              {!isEditing && (
                <p className="text-sm text-gray-600 mt-1">
                  💡 您可以先建立自定義分組，儲存工作日誌時會一併建立所有分組
                </p>
              )}
            </div>
            
            {/* 預設會議決議執行情形摘要 */}
            <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium text-gray-900">會議決議執行情形摘要</h4>
                <button
                  onClick={() => setIsIssueSelectorOpen(true)}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  選擇議題
                </button>
              </div>
              <p className="text-sm text-gray-600">
                已選擇 {selectedIssueIds.length} 個議題
              </p>
            </div>

            {/* 自定義議題分組 */}
            {issueGroups.map((group, index) => (
              <div key={index} className={`rounded-lg p-4 border shadow-sm ${
                group.isNew 
                  ? 'bg-yellow-50 border-yellow-200' 
                  : !group.id 
                    ? 'bg-blue-50 border-blue-200' 
                    : 'bg-gray-50 border-gray-300'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 flex-1">
                    {group.isNew || !group.id ? (
                      <>
                        <input
                          type="text"
                          value={group.title}
                          onChange={(e) => updateIssueGroupTitle(index, e.target.value)}
                          className="flex-1 px-3 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="請輸入分組標題"
                        />
                        <button
                          onClick={() => saveIssueGroup(index)}
                          className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                          disabled={!group.title.trim()}
                        >
                          儲存
                        </button>
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900">{group.title}</h4>
                        {!id && (
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                            待建立
                          </span>
                        )}
                        {id && group.id && (
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                            已儲存
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {!group.isNew && group.selectedIssueIds.length > 0 && id && (
                      <button
                        onClick={() => saveIssueGroup(index)}
                        className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        title="更新分組"
                      >
                        更新
                      </button>
                    )}
                    <button
                      onClick={() => deleteIssueGroup(index)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                      title="刪除分組"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    已選擇 {group.selectedIssueIds.length} 個議題
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setActiveGroupSelector(index)}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      選擇議題
                    </button>
                    {!group.isNew && group.id && (
                      <button
                        onClick={() => saveIssueGroup(index)}
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                        title="儲存議題變更"
                      >
                        儲存
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* 新增分組按鈕 */}
            <div className="text-center">
              <button
                onClick={addNewIssueGroup}
                className="inline-flex items-center px-4 py-2 text-sm text-gray-600 bg-white border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 hover:text-gray-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                新增執行概況分組
              </button>
            </div>
          </div>
        </div>

        {/* 操作按鈕 */}
        <div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center">
          <button
            onClick={() => navigate('/global-worklogs')}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            取消
          </button>
          
          <div className="flex flex-col gap-3">
            {/* 未儲存分組警告 */}
            {getUnsavedGroupsCount() > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-yellow-800">
                      您有 {getUnsavedGroupsCount()} 個未儲存的議題分組，請先儲存後再發布工作日誌
                    </span>
                  </div>
                  <button
                    onClick={saveAllUnsavedGroups}
                    className="px-3 py-1 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700"
                  >
                    儲存所有分組
                  </button>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => handleSave('DRAFT')}
                disabled={isSaving}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {isSaving ? '儲存中...' : '儲存草稿'}
              </button>
              <button
                onClick={() => handleSave('PUBLISHED')}
                disabled={isSaving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isSaving ? '發布中...' : '發布'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 預設議題選擇器 */}
      <GlobalIssueSelector
        selectedIssueIds={selectedIssueIds.filter(id => 
          id && id !== 'undefined' && id !== 'null' && id.trim() !== ''
        )}
        isOpen={isIssueSelectorOpen}
        onClose={() => setIsIssueSelectorOpen(false)}
        onSelect={handleIssueSelection}
      />

      {/* 自定義分組議題選擇器 */}
      {activeGroupSelector !== null && (
        <GlobalIssueSelector
          selectedIssueIds={issueGroups[activeGroupSelector]?.selectedIssueIds?.filter(id => 
            id && id !== 'undefined' && id !== 'null' && id.trim() !== ''
          ) || []}
          isOpen={true}
          onClose={() => setActiveGroupSelector(null)}
          onSelect={(issueIds) => handleGroupIssueSelection(activeGroupSelector, issueIds)}
        />
      )}
    </div>
  );
};

export default GlobalWorkLogEditor;
